import { useHistory } from 'react-router-dom'
import { Typography } from '@material-ui/core'
import { useTranslation } from 'react-i18next'

import { RestrictedContent } from 'components/auth/RestrictedContent/RestrictedContent'
import { ADMIN_ROLE, COMPANY_ROLE, CONTENT_CREATOR_ROLE } from 'constants/roleGroups'
import { Card } from 'components/data/Card/Card'
import { KnowledgeBaseContentType } from 'types/KnowledgeBaseContentType'
import { Label } from 'types/Label'
import { Translation } from 'types/Translation'
import * as Styled from './CardsList.styles'

interface CardsListProps {
  data: CardsListItemType[]
  emptyListInfoText: string
  onMove?: (id: number) => void
  onEdit?: (
    id: number,
    name: string,
    nameTranslations: Translation[],
    image: File | string | null,
    labels: Label[],
    dataType?: string,
  ) => void
  onDelete?: (id: number) => void
  onCopy?: (id: number) => void
}

interface CardsListItemType {
  id: number,
  name: string,
  creationDateTime: string,
  dataType?: KnowledgeBaseContentType,
  image?: string,
  nameTranslations?: Translation[],
  labels?: Label[] | null,
}

const CardsList: React.FC<CardsListProps> = ({
  data,
  emptyListInfoText,
  onMove: handleMove,
  onEdit: handleEdit,
  onDelete: handleDelete,
  onCopy: handleCopy,
}) => {
  const history = useHistory()
  const { t, i18n: { language: userLanguageBrowserCode } } = useTranslation()

  const getItemPath = (id: number, dataType?: string) => (
    `${history.location.pathname}/${dataType?.toLowerCase()}/${id}`
  )

  return (
    data.length ? (
      <Styled.Grid>
        {data.map(({
          id,
          name,
          creationDateTime,
          dataType,
          image,
          nameTranslations,
          labels,
        }) => (
          <Card
            key={id}
            title={
              nameTranslations?.find(({ languageCode }) => languageCode === userLanguageBrowserCode)
                ?.value || name
            }
            image={image}
            creationDateTime={creationDateTime}
            dataType={dataType}
            labels={labels || undefined}
            onClick={() => history.push(getItemPath(id, dataType))}
            onEdit={() =>
              handleEdit?.(id, name, nameTranslations || [], image || null, labels || [], dataType)}
            onMove={() => handleMove?.(id)}
            onDelete={() => handleDelete?.(id)}
            onCopy={() => handleCopy?.(id)}
          />
        ))}
      </Styled.Grid>
    ) : (
      <Styled.NoItemsContainer>
        <Typography variant='h5' color='primary'>
          {emptyListInfoText}
        </Typography>
        <RestrictedContent accessRoles={[CONTENT_CREATOR_ROLE, COMPANY_ROLE, ADMIN_ROLE]}>
          <Typography variant='subtitle1' color='textSecondary'>
            {t('common.emptySubtext')}
          </Typography>
        </RestrictedContent>
      </Styled.NoItemsContainer>
    )
  )
}

export type { CardsListItemType }
export { CardsList }
